import React, { useMemo } from 'react';
import { Column, useTable } from 'react-table';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableProps,
  Typography,
} from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { useCallback, useEffect } from 'react';
import { BsFillInboxesFill } from '@icon/react-icons';
import styled from 'styled-components';
import { isEqual } from 'lodash';
import * as companyService from '@api/company';
import * as personMediaService from '@api/person-media';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { useSnackbar } from 'notistack';
import { EntityType } from '@lib/deals';

interface DraggableRowsTableProps<T extends object = any> extends Omit<TableProps, 'onChange'> {
  columns: Column<T>[];
  data: T[];
  getRowId: (row: T, index: number) => string;
  onChange?: (data: T[]) => any;
  hideHeaders?: boolean;
  showDragIcon?: boolean;
  initial?: any;
  hideColumns?: any;
}

let updates = 0;

export const DraggableRowsTable = <T extends object>({
  columns,
  data,
  getRowId,
  onChange = null,
  hideHeaders = false,
  showDragIcon = true,
  initial = null,
  sectionName = "",
  company_vtr_id = "",
  person_vtr_id = "",
  multiSectionName = "",
  ...tableProps
}) => {
  const [records, setRecords] = React.useState(data);
  const [newSequencedMedia, setNewSequencedMedia] = React.useState(data);
  const hiddenColumns = useMemo(() => columns.filter(c => c.forceHide).map(c => c.id), [columns]);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    data: records,
    columns,
    getRowId,
    initialState: { ...initial, hiddenColumns },
    autoResetHiddenColumns: false,
  });
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setTimeout(() => {
      if (!isEqual(data, records)) {
        setRecords(data);
      }
    }, 0);
  }, [data]);

  useEffect(() => {
    if (!isEqual(records, data) && onChange) {
      onChange(records);
    }
  }, [records, data, onChange]);

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {

      setRecords(rcds => {
        const dragRecord = rcds[dragIndex];

        if (!dragRecord) {
            return rcds;
          }
    
        const newUpdatedRecord = {
          original_media_vtr_id: dragRecord?.original_media_vtr_id,
          old_order_id: dragRecord?.order_id,
          new_order_id: hoverIndex+1
        }
       
        setNewSequencedMedia(newUpdatedRecord)

        const updated = update(rcds, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      });

    return updated;
      });
    },
    []
  );

  useEffect(() => {
    if (sectionName === "media" && newSequencedMedia) {
      companyService.resequence(newSequencedMedia, company_vtr_id)
        .pipe(
          catchError((error) => {
            console.error('Media resequence Error:', error);
            // Show error message to the user
            // For example:
            // this.errorMessage = 'Failed to post new media record. Please try again.';
            // Or you can use a toast, alert, etc. to show the error message
            return of(); // Return an observable to avoid breaking the chain
          })
        )
        .subscribe(
          (response) => {
            enqueueSnackbar('Media resequenced Successfully!', { variant: 'success' });
          }
        );

    }
     if (sectionName === "person_media" && newSequencedMedia) {
      personMediaService.resequence(newSequencedMedia, person_vtr_id)
        .pipe(
          catchError((error) => {
            console.error('Media resequence Error:', error);
            // Show error message to the user
            // For example:
            // this.errorMessage = 'Failed to post new media record. Please try again.';
            // Or you can use a toast, alert, etc. to show the error message
            return of(); // Return an observable to avoid breaking the chain
          })
        )
        .subscribe(
          (response) => {
            enqueueSnackbar('Person Media resequenced Successfully!', { variant: 'success' });
          }
        );

    }
  }, [sectionName, newSequencedMedia])


  return (
    <DndProvider backend={HTML5Backend}>
      <TableContainer>
        <Table {...getTableProps()} {...tableProps}>
          {hideHeaders ? null : (
            <TableHead>
              {headerGroups.map(headerGroup => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {showDragIcon ? <TableCell></TableCell> : null}
                  {headerGroup.headers.map(column => (
                    <TableCell {...column.getHeaderProps()}>{column.render('Header')}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
          )}
          <TableBody {...getTableBodyProps()}>
            {!rows || (!rows.length && <EmptyRows />)}
            {rows.map((row, index) => {
              prepareRow(row);
              return (
                <Row
                  key={row.id}
                  index={index}
                  row={row}
                  moveRow={moveRow}
                  showDragIcon={showDragIcon}
                  multiSectionName ={multiSectionName}
                  {...row.getRowProps()}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </DndProvider>
  );
};

const DND_ITEM_TYPE = 'row';

const Row = ({ row, index, moveRow, showDragIcon, multiSectionName}) => {
  const dropRef = React.useRef(null);
  const dragRef = React.useRef(null);

  const [, drop] = useDrop<any, any, any>({
    accept: DND_ITEM_TYPE,
    hover(item, monitor) {
  
      if (!dropRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
    
      // Time to actually perform the action
      moveRow(dragIndex, hoverIndex);
     
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    item: { type: DND_ITEM_TYPE, index,    id: row.id, },
    type: DND_ITEM_TYPE,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;

  preview(drop(dropRef));
  drag(dragRef);

  return (
    <TableRow ref={dropRef} style={{ opacity }}>
      {showDragIcon ? (
        <>
          <TableCell ref={dragRef}>
            <DragIndicatorIcon className="drag-icon" />
            {/* style={{ cursor: 'grab' }}  */}
          </TableCell>
          {row.cells.map(cell => {
            return <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>;
          })}
        </>
      ) : (
        <>
          {row.cells.map(cell => {
            return (
              <TableCell ref={multiSectionName === EntityType.person ? null : dragRef} {...cell.getCellProps()}>
                {/* style={{ cursor: 'grab' }} */}
                {cell.render('Cell')}
              </TableCell>
            );
          })}
        </>
      )}
    </TableRow>
  );
};

const EmptyRows = styled(({ className }) => (
  <tr className={className}>
    <td colSpan={100}>
      <BsFillInboxesFill color="disabled" />
      <br />
      <Typography color="textSecondary">No Data</Typography>
    </td>
  </tr>
))`
  td {
    padding: 95px;
    text-align: center;
  }
`;
